import type { KeyboardEvent, ReactNode } from "react";

import { cn, hasRenderableNode } from "../base/utils";

export type WorkbenchVisualizationTone =
  "primary" | "secondary" | "accent" | "muted";

export interface WorkbenchScatterPoint {
  id: string;
  label: string;
  x: number;
  y: number;
  size?: number;
  description?: string;
  tone?: WorkbenchVisualizationTone;
}

export interface WorkbenchScatterAxis {
  label: string;
  min?: number;
  max?: number;
}

export interface WorkbenchScatterThresholds {
  x?: number;
  y?: number;
}

export interface WorkbenchScatterPlotProps {
  points: WorkbenchScatterPoint[];
  xAxis: WorkbenchScatterAxis;
  yAxis: WorkbenchScatterAxis;
  thresholds?: WorkbenchScatterThresholds;
  selectedPointId?: string;
  onPointSelect?: (point: WorkbenchScatterPoint) => void;
  width?: number;
  height?: number;
  emptyState?: ReactNode;
  ariaLabel?: string;
  className?: string;
}

const toneClassNames: Record<WorkbenchVisualizationTone, string> = {
  primary: "fill-primary stroke-primary",
  secondary: "fill-secondary stroke-secondary-foreground",
  accent: "fill-accent stroke-accent-foreground",
  muted: "fill-muted stroke-muted-foreground",
};

const finiteValues = (values: number[]) => values.filter(Number.isFinite);

const getDomain = (values: number[], min?: number, max?: number) => {
  const candidates = finiteValues(values);
  const lower = Number.isFinite(min) ? Number(min) : Math.min(...candidates, 0);
  const upper = Number.isFinite(max) ? Number(max) : Math.max(...candidates, 1);
  if (lower === upper) return [lower, upper + 1] as const;
  return lower < upper ? ([lower, upper] as const) : ([upper, lower] as const);
};

const scale = (
  value: number,
  domain: readonly [number, number],
  start: number,
  end: number,
) => start + ((value - domain[0]) / (domain[1] - domain[0])) * (end - start);

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const activatePoint = (
  event: KeyboardEvent<SVGGElement>,
  point: WorkbenchScatterPoint,
  onPointSelect?: (point: WorkbenchScatterPoint) => void,
) => {
  if (!onPointSelect || (event.key !== "Enter" && event.key !== " ")) return;
  event.preventDefault();
  onPointSelect(point);
};

export function WorkbenchScatterPlot({
  points,
  xAxis,
  yAxis,
  thresholds,
  selectedPointId,
  onPointSelect,
  width = 900,
  height = 420,
  emptyState = null,
  ariaLabel = `${xAxis.label} by ${yAxis.label}`,
  className,
}: WorkbenchScatterPlotProps) {
  const validPoints = points.filter(
    (point) => Number.isFinite(point.x) && Number.isFinite(point.y),
  );
  if (!validPoints.length) {
    return hasRenderableNode(emptyState) ? <>{emptyState}</> : null;
  }

  const padding = 56;
  const xDomain = getDomain(
    validPoints.map((point) => point.x),
    xAxis.min,
    xAxis.max,
  );
  const yDomain = getDomain(
    validPoints.map((point) => point.y),
    yAxis.min,
    yAxis.max,
  );
  const sizeDomain = getDomain(validPoints.map((point) => point.size ?? 1));
  const plotLeft = padding;
  const plotRight = width - padding;
  const plotTop = padding;
  const plotBottom = height - padding;
  const xThreshold = Number.isFinite(thresholds?.x)
    ? scale(Number(thresholds?.x), xDomain, plotLeft, plotRight)
    : undefined;
  const yThreshold = Number.isFinite(thresholds?.y)
    ? scale(Number(thresholds?.y), yDomain, plotBottom, plotTop)
    : undefined;

  return (
    <div
      data-monkeys-component="workbench-scatter-plot"
      className={cn(
        "min-w-0 overflow-x-auto rounded-lg border border-border/70 bg-card p-3",
        className,
      )}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="min-w-[42rem]"
        role="img"
        aria-label={ariaLabel}
      >
        <line
          x1={plotLeft}
          y1={plotBottom}
          x2={plotRight}
          y2={plotBottom}
          className="stroke-border"
        />
        <line
          x1={plotLeft}
          y1={plotTop}
          x2={plotLeft}
          y2={plotBottom}
          className="stroke-border"
        />
        {xThreshold === undefined ? null : (
          <line
            x1={clamp(xThreshold, plotLeft, plotRight)}
            y1={plotTop}
            x2={clamp(xThreshold, plotLeft, plotRight)}
            y2={plotBottom}
            className="stroke-muted-foreground/50"
            strokeDasharray="5 5"
          />
        )}
        {yThreshold === undefined ? null : (
          <line
            x1={plotLeft}
            y1={clamp(yThreshold, plotTop, plotBottom)}
            x2={plotRight}
            y2={clamp(yThreshold, plotTop, plotBottom)}
            className="stroke-muted-foreground/50"
            strokeDasharray="5 5"
          />
        )}
        <text
          x={width / 2}
          y={height - 12}
          textAnchor="middle"
          className="fill-muted-foreground text-xs"
        >
          {xAxis.label}
        </text>
        <text
          x="16"
          y={height / 2}
          textAnchor="middle"
          transform={`rotate(-90 16 ${height / 2})`}
          className="fill-muted-foreground text-xs"
        >
          {yAxis.label}
        </text>
        {validPoints.map((point) => {
          const x = clamp(
            scale(point.x, xDomain, plotLeft, plotRight),
            plotLeft,
            plotRight,
          );
          const y = clamp(
            scale(point.y, yDomain, plotBottom, plotTop),
            plotTop,
            plotBottom,
          );
          const radius =
            point.size === undefined
              ? 9
              : clamp(scale(point.size, sizeDomain, 7, 18), 7, 18);
          const selected = selectedPointId === point.id;
          return (
            <g
              key={point.id}
              transform={`translate(${x}, ${y})`}
              role={onPointSelect ? "button" : undefined}
              tabIndex={onPointSelect ? 0 : undefined}
              aria-label={`${point.label}: ${xAxis.label} ${point.x}, ${yAxis.label} ${point.y}`}
              aria-pressed={onPointSelect ? selected : undefined}
              onClick={onPointSelect ? () => onPointSelect(point) : undefined}
              onKeyDown={(event) => activatePoint(event, point, onPointSelect)}
              className={cn(onPointSelect && "cursor-pointer outline-none")}
            >
              <circle
                r={radius}
                className={cn(
                  "stroke-1 opacity-80 transition-opacity duration-150 motion-reduce:transition-none",
                  toneClassNames[point.tone || "primary"],
                  selected ? "opacity-100 stroke-[3]" : "hover:opacity-100",
                )}
              />
              <text
                y={-radius - 7}
                textAnchor="middle"
                className="pointer-events-none fill-foreground text-[0.6875rem] font-semibold"
              >
                {point.label.slice(0, 24)}
              </text>
              <title>{point.description || point.label}</title>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
